import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
// import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";

// Player-facing avatar-page copy — required, never blank on save. The
// actual default *text* lives in Kpop-Backend/themes/themesets.js (theme0)
// and is fetched dynamically via fetchThemeData, not hardcoded here.
//
// Max lengths are a frontend/admin-UI concern though, so they stay here.
// Note: the upload instruction's own default text is ~114 characters — a
// 30-character cap (as for the other short labels) would cut it off before
// it even loads, so this one gets a larger limit instead.
const GAME_TEXT_MAX_LENGTHS = {
  managerNameLabel: 30,
  uploadInstructionText: 200,
  animatedAvatarText: 150,
  superheroNameLabel: 30,
  catchyPhraseText: 30,
};

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  // Basic theme details: Title, Step 1 Text, Step 2 Text
  const [title, setTitle] = useState("");
  const [step1Text, setStep1Text] = useState("");
  const [step2Text, setStep2Text] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [galleryEnabled, setGalleryEnabled] = useState(true);

  // Player-facing avatar-page text — editable, required, never blank.
  // Populated from theme data once it loads (see below); the backend
  // supplies a theme0 default for any theme that hasn't saved its own yet.
  const [managerNameLabel, setManagerNameLabel] = useState("");
  const [uploadInstructionText, setUploadInstructionText] = useState("");
  const [animatedAvatarText, setAnimatedAvatarText] = useState("");
  const [superheroNameLabel, setSuperheroNameLabel] = useState("");
  const [catchyPhraseText, setCatchyPhraseText] = useState("");

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#000000",
    button_text_color: "#ffffff",
    leaderboard_color: "#000000",
    table_color1: "#000000",
    table_color2: "#ffffff",
  });

  useEffect(() => {
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    if (!source) return;
    setColors((prev) => ({
      text_color: source.text_color ?? prev.text_color,
      button_color: source.button_color ?? prev.button_color,
      button_text_color: source.button_text_color ?? prev.button_text_color,
      leaderboard_color: source.leaderboard_color ?? prev.leaderboard_color,
      table_color1: source.table_color1 ?? prev.table_color1,
      table_color2: source.table_color2 ?? prev.table_color2,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate rules + basic theme details
  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setTitle(data.follow_text || "");
    setStep1Text(data.step1_text || "");
    setStep2Text(data.step2_text || "");
    setGalleryEnabled(data.gallery_enabled !== false);
    // The backend already merges in the theme0 default when a field hasn't
    // been saved yet (see fetchThemeData.js), so these should always arrive
    // populated — the "" fallback here is just a defensive last resort.
    setManagerNameLabel(data.manager_name_label || "");
    setUploadInstructionText(data.upload_instruction_text || "");
    setAnimatedAvatarText(data.animated_avatar_text || "");
    setSuperheroNameLabel(data.superhero_name_label || "");
    setCatchyPhraseText(data.catchy_phrase_text || "");
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleTitleChange = (val) => {
    if (val.length > 100) {
      Swal.fire("Limit Exceeded", "Heading can be at most 100 characters. Extra text has been trimmed.", "warning");
      val = val.slice(0, 100);
    }
    setTitle(val);
  };
  

  // --- submit all ---
  const handleSubmit = async () => {
    const trimmedRules = rules.map((r) => (r ?? "").trim());

    // Ensure there is at least one rule and no rule row is left blank.
    if (trimmedRules.length === 0 || trimmedRules.every((r) => !r)) {
      await Swal.fire(
        "At Least One Rule Required",
        "Please enter at least one rule.",
        "warning"
      );
      return;
    }

    const firstEmptyIndex = trimmedRules.findIndex((r) => !r);
    if (firstEmptyIndex !== -1) {
      await Swal.fire(
        "Rule Text Required",
        `Rule #${firstEmptyIndex + 1} cannot be empty.`,
        "warning"
      );
      return;
    }

    // Player-facing avatar-page text is required — none of it may be saved
    // blank, since it's shown directly on the game's avatar page.
    const gameTextFields = [
      ["Manager's Name label", managerNameLabel],
      ["Upload instruction message", uploadInstructionText],
      ["Animated avatar text", animatedAvatarText],
      ["Superhero Name label", superheroNameLabel],
      ["Catchy-phrase hint", catchyPhraseText],
    ];
    const emptyField = gameTextFields.find(([, value]) => !value.trim());
    if (emptyField) {
      await Swal.fire(
        "Text Required",
        `"${emptyField[0]}" cannot be empty.`,
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        follow_text: title,
        step1_text: step1Text,
        step2_text: step2Text,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_text_color: colors.button_text_color,
        leaderboard_color: colors.leaderboard_color,
        table_color1: colors.table_color1,
        table_color2: colors.table_color2,
        gallery_enabled: galleryEnabled,
        manager_name_label: managerNameLabel.trim(),
        upload_instruction_text: uploadInstructionText.trim(),
        animated_avatar_text: animatedAvatarText.trim(),
        superhero_name_label: superheroNameLabel.trim(),
        catchy_phrase_text: catchyPhraseText.trim(),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/themeUpdate"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Details
                </div>
                <div className="form-group-v1">
                  <label>Heading</label>
                  <textarea
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Heading"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={100}
                  />
                  <div className="code">* Maximum 100 characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Manager's Name (field label)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={managerNameLabel}
                    maxLength={GAME_TEXT_MAX_LENGTHS.managerNameLabel}
                    onChange={(e) => setManagerNameLabel(e.target.value)}
                  />
                  <div className="code">{managerNameLabel.length}/{GAME_TEXT_MAX_LENGTHS.managerNameLabel}</div>
                </div>

                <div className="form-group-v1">
                  <label>Superhero Name (field label)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={superheroNameLabel}
                    maxLength={GAME_TEXT_MAX_LENGTHS.superheroNameLabel}
                    onChange={(e) => setSuperheroNameLabel(e.target.value)}
                  />
                  <div className="code">{superheroNameLabel.length}/{GAME_TEXT_MAX_LENGTHS.superheroNameLabel}</div>
                </div>

                <div className="form-group-v1">
                  <label>Catchy-phrase hint</label>
                  <input
                    type="text"
                    className="form-control"
                    value={catchyPhraseText}
                    maxLength={GAME_TEXT_MAX_LENGTHS.catchyPhraseText}
                    onChange={(e) => setCatchyPhraseText(e.target.value)}
                  />
                  <div className="code">{catchyPhraseText.length}/{GAME_TEXT_MAX_LENGTHS.catchyPhraseText}</div>
                </div>

                <div className="form-group-v1">
                  <label>Upload instruction message</label>
                  <textarea
                    className="form-control rules-textarea"
                    rows={2}
                    value={uploadInstructionText}
                    maxLength={GAME_TEXT_MAX_LENGTHS.uploadInstructionText}
                    onChange={(e) => setUploadInstructionText(e.target.value)}
                  />
                  <div className="code">{uploadInstructionText.length}/{GAME_TEXT_MAX_LENGTHS.uploadInstructionText}</div>
                </div>

                <div className="form-group-v1">
                  <label>Animated avatar text</label>
                  <textarea
                    className="form-control rules-textarea"
                    rows={2}
                    value={animatedAvatarText}
                    maxLength={GAME_TEXT_MAX_LENGTHS.animatedAvatarText}
                    onChange={(e) => setAnimatedAvatarText(e.target.value)}
                  />
                  <div className="code">{animatedAvatarText.length}/{GAME_TEXT_MAX_LENGTHS.animatedAvatarText}</div>
                </div>
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 6 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_text_color}
                          onChange={handleColorChange("button_text_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-images"></i> &nbsp; Gallery
                </div>
                <div className="gallery-toggle-row">
                  <span>{galleryEnabled ? "Gallery is shown to players" : "Gallery is hidden from players"}</span>
                  <label className="theme-toggle">
                    <input
                      type="checkbox"
                      checked={galleryEnabled}
                      onChange={(e) => setGalleryEnabled(e.target.checked)}
                    />
                    <span className="theme-toggle-slider" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>

          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
